import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Sparkles, Play, Pause, X, Printer, Stethoscope, Download, Volume2, VolumeX, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const CYCLE = [
  { label: "Breathe in", secs: 4, scale: 1 },
  { label: "Hold", secs: 7, scale: 1 },
  { label: "Breathe out", secs: 8, scale: 0.55 },
];
const ROUNDS = 4;

function BreathingTimer({ onClose }) {
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const [done, setDone] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [count, setCount] = useState(CYCLE[0].secs);
  const [round, setRound] = useState(1);
  const mutedRef = useRef(false);
  const st = useRef({ phaseIdx: 0, count: CYCLE[0].secs, round: 1 });

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const speak = (text) => {
    if (mutedRef.current || typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.78;
      u.pitch = 1;
      u.volume = 1;
      window.speechSynthesis.speak(u);
    } catch (e) { /* speech not available */ }
  };

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const s = st.current;
      if (s.count > 1) {
        s.count -= 1;
      } else {
        let np = s.phaseIdx + 1;
        if (np >= CYCLE.length) {
          np = 0;
          s.round += 1;
          if (s.round > ROUNDS) {
            clearInterval(id);
            s.phaseIdx = 0;
            s.count = CYCLE[0].secs;
            setRunning(false);
            setDone(true);
            setPhaseIdx(0);
            speak("Well done. Take a moment to notice how you feel.");
            return;
          }
        }
        s.phaseIdx = np;
        s.count = CYCLE[np].secs;
        speak(CYCLE[np].label);
      }
      setPhaseIdx(s.phaseIdx);
      setCount(s.count);
      setRound(s.round);
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => () => { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {} }, []);

  const begin = () => {
    if (done) {
      st.current = { phaseIdx: 0, count: CYCLE[0].secs, round: 1 };
      setPhaseIdx(0);
      setCount(CYCLE[0].secs);
      setRound(1);
      setDone(false);
    }
    setStarted(true);
    speak(CYCLE[st.current.phaseIdx].label);
    setRunning(true);
  };

  const pause = () => {
    setRunning(false);
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (next) { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {} }
      return next;
    });
  };

  const cur = CYCLE[phaseIdx];
  const idle = !started || done;
  const scale = idle ? 0.55 : cur.scale;
  const dur = idle ? 0.6 : cur.secs;
  const label = done ? "Well done" : !started ? "Ready" : cur.label;
  const btnLabel = done ? "Start again" : running ? "Pause" : started ? "Resume" : "Begin";

  return (
    <div className="fixed inset-0 z-[70] bg-brand flex flex-col items-center justify-center px-6" data-testid="breathing-timer">
      <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors" data-testid="breathing-close">
        <X size={26} />
      </button>
      <button onClick={toggleMute} className="absolute top-6 left-6 text-white/70 hover:text-white transition-colors" data-testid="breathing-mute" aria-label={muted ? "Unmute voice guide" : "Mute voice guide"}>
        {muted ? <VolumeX size={24} /> : <Volume2 size={24} />}
      </button>

      <p className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase" data-testid="breathing-pattern">4 · 7 · 8 Breathing</p>

      <div className="mt-10 w-64 h-64 flex items-center justify-center">
        <div
          className="w-64 h-64 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: "rgba(255,255,255,0.12)",
            transform: `scale(${scale})`,
            transition: `transform ${dur}s ease-in-out`,
          }}
        >
          <div className="w-40 h-40 rounded-full flex flex-col items-center justify-center text-center" style={{ backgroundColor: "rgba(255,255,255,0.16)" }}>
            <span className="text-white font-serif text-2xl leading-tight" data-testid="breathing-phase">{label}</span>
            {started && !done && (
              <span className="text-white/70 text-3xl font-light mt-1" data-testid="breathing-count">{count}</span>
            )}
          </div>
        </div>
      </div>

      {started && !done ? (
        <p className="text-white/70 text-sm mt-8 font-medium" data-testid="breathing-round">Round {Math.min(round, ROUNDS)} of {ROUNDS}</p>
      ) : (
        <p className="text-white/50 text-sm mt-8 text-center max-w-xs">A calming voice will guide you. Tap begin, then follow the circle.</p>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={running ? pause : begin}
          data-testid="breathing-toggle"
          className="bg-white text-brand font-semibold px-8 py-3.5 rounded-full flex items-center gap-2 active:scale-95 transition-transform"
        >
          {running ? <><Pause size={18} /> Pause</> : done ? <><RotateCcw size={18} /> Start again</> : <><Play size={18} /> {btnLabel}</>}
        </button>
      </div>

      {done && (
        <p className="text-white/70 text-sm mt-6 text-center max-w-xs" data-testid="breathing-done">
          Beautifully done. Come back to this whenever you need to settle.
        </p>
      )}
      <p className="text-white/40 text-xs mt-6 text-center max-w-xs">Stop any time you feel light-headed.</p>
    </div>
  );
}

function ToolCard({ item }) {
  const [open, setOpen] = useState(false);
  const [breathe, setBreathe] = useState(false);
  const navigate = useNavigate();

  const openCard = () => {
    const next = !open;
    setOpen(next);
    if (next) api.post(`/toolkit/${item.slug}/use`).catch(() => {});
  };

  if (item.tool === "planner") {
    return (
      <div className="bg-white rounded-2xl card-shadow p-5" data-testid={`tool-${item.slug}`}>
        <div className="text-accent-c text-xs font-semibold">{item.category}</div>
        <div className="font-semibold text-ink mt-0.5">{item.title}</div>
        <div className="text-ink-soft text-sm mt-1">{item.description}</div>
        <button
          data-testid={`open-planner-${item.slug}`}
          onClick={() => { api.post(`/toolkit/${item.slug}/use`).catch(() => {}); navigate("/doctor-planner"); }}
          className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <Stethoscope size={16} /> Open planner
        </button>
      </div>
    );
  }

  if (item.tool === "diary") {
    return (
      <div className="bg-white rounded-2xl card-shadow p-5" data-testid={`tool-${item.slug}`}>
        <div className="text-accent-c text-xs font-semibold">{item.category}</div>
        <div className="font-semibold text-ink mt-0.5">{item.title}</div>
        <div className="text-ink-soft text-sm mt-1">{item.description}</div>
        <button
          data-testid={`open-diary-${item.slug}`}
          onClick={() => { api.post(`/toolkit/${item.slug}/use`).catch(() => {}); navigate("/hot-flash-diary"); }}
          className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <Printer size={16} /> Open printable diary
        </button>
      </div>
    );
  }

  if (item.tool === "sleep-journal") {
    return (
      <div className="bg-white rounded-2xl card-shadow p-5" data-testid={`tool-${item.slug}`}>
        <div className="text-accent-c text-xs font-semibold">{item.category}</div>
        <div className="font-semibold text-ink mt-0.5">{item.title}</div>
        <div className="text-ink-soft text-sm mt-1">{item.description}</div>
        <button
          data-testid={`open-sleep-journal-${item.slug}`}
          onClick={() => { api.post(`/toolkit/${item.slug}/use`).catch(() => {}); navigate("/sleep-journal"); }}
          className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <Printer size={16} /> Open printable journal
        </button>
      </div>
    );
  }

  if (item.tool === "printable") {
    return (
      <div className="bg-white rounded-2xl card-shadow p-5" data-testid={`tool-${item.slug}`}>
        <div className="text-accent-c text-xs font-semibold">{item.category}</div>
        <div className="font-semibold text-ink mt-0.5">{item.title}</div>
        <div className="text-ink-soft text-sm mt-1">{item.description}</div>
        <button
          data-testid={`open-printable-${item.slug}`}
          onClick={() => { api.post(`/toolkit/${item.slug}/use`).catch(() => {}); navigate(`/printable/${item.slug}`); }}
          className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <Printer size={16} /> Open printable
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl card-shadow overflow-hidden" data-testid={`tool-${item.slug}`}>
        <button onClick={openCard} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
          <div>
            <div className="text-accent-c text-xs font-semibold">{item.category}</div>
            <div className="font-semibold text-ink mt-0.5">{item.title}</div>
            <div className="text-ink-soft text-sm mt-1">{item.description}</div>
          </div>
          <ChevronDown size={20} className={`text-ink-muted flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="px-5 pb-5">
            <ul className="space-y-2">
              {item.content.map((b, i) => (
                <li key={i} className="flex gap-3 text-ink-soft text-sm">
                  <span className="text-brand font-bold flex-shrink-0">{i + 1}.</span> {b}
                </li>
              ))}
            </ul>
            {item.tool === "breathing-timer" && (
              <button
                data-testid={`open-breathing-${item.slug}`}
                onClick={() => setBreathe(true)}
                className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
              >
                <Play size={16} /> Start guided breathing
              </button>
            )}
            {item.tool === "workbook" && (
              <button
                data-testid={`print-workbook-${item.slug}`}
                onClick={() => window.print()}
                className="mt-4 w-full border-2 border-[var(--brand)] text-brand font-semibold py-3 rounded-full flex items-center justify-center gap-2"
              >
                <Printer size={16} /> Print / Download workbook
              </button>
            )}
            {item.document_url && (
              <a
                data-testid={`download-doc-${item.slug}`}
                href={`${process.env.REACT_APP_BACKEND_URL}${item.document_url}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { api.post(`/toolkit/${item.slug}/use`).catch(() => {}); }}
                className="mt-4 w-full bg-brand text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2"
              >
                <Download size={16} /> Download {item.document_name ? "" : "document"}
              </a>
            )}
          </div>
        )}
      </div>
      {breathe && <BreathingTimer onClose={() => setBreathe(false)} />}
    </>
  );
}

export default function Toolkit() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/toolkit").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  const recIds = new Set(data.recommended.map((r) => r.id));
  const rest = data.items.filter((i) => !recIds.has(i.id));

  return (
    <div className="px-5 pt-8 animate-fade-rise" data-testid="toolkit-page">
      <p className="overline-label">Your toolkit</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">Calm tools, ready anytime</h1>
      <p className="text-ink-soft text-sm mt-2">Educational, self management resources, not medical advice.</p>

      {data.recommended.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-1.5 mb-3">
            <Sparkles size={15} className="text-accent-c" />
            <p className="overline-label">Recommended for you</p>
          </div>
          <div className="space-y-3">
            {data.recommended.map((item) => (
              <ToolCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <p className="overline-label mb-3 block">All tools</p>
        <div className="space-y-3">
          {rest.map((item) => (
            <ToolCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}
